import { useEffect, useRef, useState, useCallback } from 'react'
import { X, Smartphone, QrCode, Loader2, CheckCircle2, XCircle, ExternalLink, ArrowRight } from 'lucide-react'
import { api, type SignInPayload } from '@/lib/api'

type SignInState = 'waiting' | 'scanned' | 'verifying' | 'signed' | 'rejected' | 'expired' | 'error'

interface SignInDialogProps {
  payload: SignInPayload
  onSuccess: (sessionId: string, walletAddress: string) => void
  onClose: () => void
}

const IS_MOBILE = typeof navigator !== 'undefined'
  && /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

export function SignInDialog({ payload, onSuccess, onClose }: SignInDialogProps) {
  const [state, setState] = useState<SignInState>('waiting')
  const [showQr, setShowQr] = useState(!IS_MOBILE)
  const [appOpened, setAppOpened] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)
  const closedRef = useRef(false)
  const verifiedRef = useRef(false)

  const verifyPayload = useCallback(async () => {
    if (verifiedRef.current) return
    try {
      setState('verifying')
      const { data } = await api.checkSignIn(payload.uuid)
      if (data.status === 'signed') {
        verifiedRef.current = true
        setState('signed')
        onSuccess(data.sessionId, data.walletAddress)
      } else if (data.status === 'rejected') {
        setState('rejected')
      } else if (data.status === 'expired') {
        setState('expired')
      } else {
        // Still pending — revert to waiting/scanned
        setState(appOpened ? 'scanned' : 'waiting')
      }
    } catch {
      setState('error')
    }
  }, [payload.uuid, onSuccess, appOpened])

  // WebSocket for real-time updates
  useEffect(() => {
    closedRef.current = false
    verifiedRef.current = false

    const connectWs = () => {
      if (closedRef.current || verifiedRef.current) return
      const ws = new WebSocket(payload.websocketUrl)
      wsRef.current = ws

      ws.onmessage = (event) => {
        if (closedRef.current) return

        let msg: Record<string, unknown>
        try {
          msg = JSON.parse(event.data as string)
        } catch {
          return
        }

        if (msg.opened === true) {
          setState('scanned')
          setAppOpened(true)
        }

        if (msg.signed === true) {
          ws.close()
          verifyPayload()
        }

        if (msg.signed === false) {
          setState('rejected')
          ws.close()
        }

        if (msg.expired === true) {
          setState('expired')
          ws.close()
        }
      }

      ws.onclose = () => {
        if (!closedRef.current && !verifiedRef.current) {
          setTimeout(connectWs, 3000)
        }
      }
    }

    connectWs()

    const timeout = setTimeout(() => {
      if (!closedRef.current && !verifiedRef.current) {
        setState('expired')
        wsRef.current?.close()
      }
    }, 5 * 60 * 1000)

    return () => {
      closedRef.current = true
      clearTimeout(timeout)
      wsRef.current?.close()
    }
  }, [payload.websocketUrl, verifyPayload])

  // Visibility/focus fallback: poll when user returns from Xaman app
  useEffect(() => {
    const poll = () => {
      if (!verifiedRef.current && !closedRef.current) {
        verifyPayload()
      }
    }

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') poll()
    })
    window.addEventListener('focus', poll)
    window.addEventListener('pageshow', poll)

    return () => {
      document.removeEventListener('visibilitychange', poll)
      window.removeEventListener('focus', poll)
      window.removeEventListener('pageshow', poll)
    }
  }, [verifyPayload])

  const isTerminal = state === 'signed' || state === 'rejected' || state === 'expired' || state === 'error'
  const isActive = state === 'waiting' || state === 'scanned' || state === 'verifying'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget && isTerminal) onClose() }}
      role="dialog"
      aria-modal="true"
      aria-label="Sign in with Xaman"
    >
      <div className="relative w-full max-w-sm rounded-2xl border border-gray-700 bg-gray-900 p-6 shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-white transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="text-center space-y-5">
          <h2 className="text-lg font-semibold text-white">Sign in with Xaman</h2>

          {/* ── Active states ── */}
          {isActive && (
            <>
              {/* MOBILE: Deep-link CTA primary */}
              {IS_MOBILE && (
                <a
                  href={payload.deepLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setAppOpened(true)}
                  className="flex items-center justify-center gap-3 w-full px-6 py-4 rounded-xl bg-gradient-to-r from-xrpl-blue to-xrpl-purple font-semibold text-white shadow-lg shadow-xrpl-blue/20 hover:shadow-xrpl-blue/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
                >
                  <ExternalLink className="h-5 w-5" />
                  Open in Xaman App
                </a>
              )}

              {/* QR Code — primary on desktop, optional on mobile */}
              {IS_MOBILE && !showQr && !appOpened && (
                <button
                  type="button"
                  onClick={() => setShowQr(true)}
                  className="text-sm text-gray-500 hover:text-gray-300 transition-colors flex items-center gap-1.5 mx-auto"
                >
                  <QrCode className="h-4 w-4" />
                  Show QR code instead
                </button>
              )}

              {(showQr || !IS_MOBILE) && !appOpened && (
                <div className="space-y-3">
                  {IS_MOBILE && (
                    <p className="text-xs text-gray-500">Or scan with another device:</p>
                  )}
                  <div className="relative mx-auto w-52 h-52 rounded-xl overflow-hidden bg-white p-2">
                    <img
                      src={payload.qrUrl}
                      alt="Scan this QR code with Xaman"
                      className="w-full h-full object-contain"
                      width={208}
                      height={208}
                    />
                    {state === 'scanned' && (
                      <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-2">
                        <Loader2 className="h-8 w-8 text-xrpl-blue animate-spin" />
                        <span className="text-xs text-white font-medium">Awaiting approval</span>
                      </div>
                    )}
                  </div>
                  {!IS_MOBILE && (
                    <div className="flex items-center gap-2 justify-center text-xs text-gray-500">
                      <QrCode className="h-3.5 w-3.5" />
                      Scan the QR code with your Xaman app
                    </div>
                  )}
                </div>
              )}

              {/* Status + mobile instructions after app was opened */}
              {appOpened && IS_MOBILE && state !== 'verifying' && (
                <div className="p-4 rounded-xl bg-gray-800/50 border border-gray-700 space-y-3 text-left">
                  <p className="text-sm text-gray-300 font-medium">Opened Xaman?</p>
                  <ol className="text-sm text-gray-400 space-y-1.5 list-decimal list-inside">
                    <li>Approve the sign-in request in Xaman</li>
                    <li>Tap <strong className="text-gray-200">"Open {window.location.hostname}"</strong> in Xaman</li>
                    <li>Or come back here manually</li>
                  </ol>
                </div>
              )}

              {/* Status indicator */}
              <div className="flex items-center gap-2 justify-center text-sm text-gray-400">
                {state === 'waiting' && !appOpened && (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin text-xrpl-blue" />
                    Waiting for Xaman&hellip;
                  </>
                )}
                {state === 'waiting' && appOpened && (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin text-xrpl-blue" />
                    Approve in Xaman, then return here
                  </>
                )}
                {state === 'scanned' && (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin text-xrpl-purple" />
                    Approve the sign-in in your Xaman app
                  </>
                )}
                {state === 'verifying' && (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin text-green-400" />
                    Verifying&hellip;
                  </>
                )}
              </div>

              {/* Manual verify button — for when WebSocket/visibility fails */}
              {appOpened && state !== 'verifying' && (
                <button
                  type="button"
                  onClick={verifyPayload}
                  className="flex items-center justify-center gap-2 w-full px-5 py-3 rounded-xl bg-gray-800 hover:bg-gray-700 border border-gray-600 text-sm font-medium text-white transition-colors"
                >
                  <ArrowRight className="h-4 w-4" />
                  I already approved — continue
                </button>
              )}

              {/* Desktop: small deep link fallback */}
              {!IS_MOBILE && (
                <a
                  href={payload.deepLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-300 transition-colors"
                >
                  <Smartphone className="h-4 w-4" />
                  Open in Xaman App
                </a>
              )}
            </>
          )}

          {/* ── Signed ── */}
          {state === 'signed' && (
            <div className="py-8 space-y-3">
              <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto" />
              <p className="text-green-400 font-medium">Successfully signed in!</p>
            </div>
          )}

          {/* ── Rejected ── */}
          {state === 'rejected' && (
            <div className="py-8 space-y-3">
              <XCircle className="h-12 w-12 text-red-500 mx-auto" />
              <p className="text-red-400 font-medium">Sign-in was rejected</p>
              <p className="text-sm text-gray-500">You declined the request in Xaman.</p>
            </div>
          )}

          {/* ── Expired ── */}
          {state === 'expired' && (
            <div className="py-8 space-y-3">
              <XCircle className="h-12 w-12 text-yellow-500 mx-auto" />
              <p className="text-yellow-400 font-medium">Request expired</p>
              <p className="text-sm text-gray-500">Please try connecting again.</p>
            </div>
          )}

          {/* ── Error ── */}
          {state === 'error' && (
            <div className="py-8 space-y-3">
              <XCircle className="h-12 w-12 text-red-500 mx-auto" />
              <p className="text-red-400 font-medium">Something went wrong</p>
              <p className="text-sm text-gray-500">Please try again.</p>
            </div>
          )}

          {isTerminal && (
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-sm font-medium text-white transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
